import { inject, Injectable } from '@angular/core';
import { produce } from 'immer';
import { map, Observable } from 'rxjs';

import { CanvasElement, CanvasElementId, WrapperField } from '@blaze-runtime/engine-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { BaseChildrenHandlerService } from '../../canvas-elements/io-data/default-handlers/base-children-handler.service';
import {
  ComponentChildrenConfig,
  ComponentChildrenHandler,
} from '../../canvas-elements/io-data/io-data.models';
import {
  FormListRenderElementInstance,
  InputsCache,
} from '../../canvas-elements/models/render-element.models';
import { FormChildOutputService } from '../../services/forms/runtime/public-api';

@Injectable({ providedIn: 'root' })
export class FormListChildrenHandlerService
  extends BaseChildrenHandlerService
  implements ComponentChildrenHandler
{
  private readonly formChildOutput = inject(FormChildOutputService);
  buildInputsCache(element: CanvasElement): InputsCache {
    const inputsCache = Object.entries(getWrapperRefs(element))
      .filter(([ref]) => WrapperField.isRefId(ref))
      .reduce(
        (acc, [ref, id]) => {
          acc[id] = this.schemaService.getColumnName(WrapperField.getValue(ref)) ?? false;
          return acc;
        },
        {} as Record<CanvasElementId, string | boolean>,
      );
    return inputsCache;
  }

  connectChildren({
    container,
    childrenUpdates$,
    inputsCache,
    formInstance,
  }: ComponentChildrenConfig): Observable<void> {
    const instance = formInstance as unknown as FormListRenderElementInstance;
    const dataValueFieldCache: Record<string, string | boolean> = {};

    return childrenUpdates$.pipe(
      map(({ event, formElement, inputElement }) => {
        if (!this.formChildOutput.contributesToFormOutput(inputElement.type)) {
          return;
        }
        if (event.rowContext === undefined) {
          return;
        }

        container = formElement;

        const modelField = inputsCache[event.elementId];
        if (typeof modelField === 'boolean') {
          return;
        }

        let dataValueField: string | boolean = dataValueFieldCache[event.elementId];

        if (!dataValueField) {
          dataValueField = this.getDataValueField(inputElement, event.data);
          if (typeof dataValueField === 'string') {
            dataValueFieldCache[event.elementId] = dataValueField;
          }
        }

        const instanceSchema =
          instance.schema || this.getElementOutputSchema(formElement, 'change', formInstance);
        const dataRows = instance.value ?? [];

        const { value: inputValue, triggerFormulasRefresh } = this.getChildValue({
          formInstance,
          dataValueField,
          modelField,
          eventData: event.data,
          formDataBindings: container.props.data,
          inputElement,
          instanceValue: dataRows,
          instanceSchema,
          formContext: undefined,
          preventLinksExtraction: true,
        });

        const rowIndex = dataRows.findIndex(
          (r) => r[instance.rowIdColumn] === event.rowContext?.rowId,
        );
        if (rowIndex === -1 || !dataRows[rowIndex]) {
          return;
        }

        const { value: currentValue } = this.getChildValue({
          formInstance,
          dataValueField,
          modelField,
          eventData: dataRows?.[rowIndex]?.[modelField],
          formDataBindings: container.props.data,
          inputElement,
          instanceValue: dataRows,
          instanceSchema,
          formContext: undefined,
          preventLinksExtraction: true,
        });

        if (isDeepEqual(currentValue, inputValue)) {
          return;
        }

        instance.value = produce(dataRows, (draft) => {
          draft[rowIndex][modelField] = inputValue;
        });

        instance.valueUpdated(event.rowContext, modelField);
        instance.validateValue(event.rowContext);
        this.updateDataContainerOutput(
          container,
          inputElement,
          instance,
          event,
          triggerFormulasRefresh,
        );
      }),
    );
  }
}
